import React, { useState } from 'react';
import { toast, Card } from '../V2Components';
import FieldErrorList from './FieldErrorList';

const EMPTY_FORM = {
    label: '',
    location: '',
    total_slots: '',
    main_breaker_amps: '',
    notes: '',
};

/**
 * Create/edit form for a breaker Panel, rendered as an inline in-page card
 * (matching the Add Utility pattern in HouseDetails.jsx) — the form sits in the
 * page flow below the tab header, so the board behind stays put with no overlay.
 * Returns null when closed; the parent (ElectricalTab) owns open/close state.
 *
 * Modes (disambiguated by `panel.id`, like RoomPaintModal):
 *   - Create (panel=null): blank form.
 *   - Edit (panel={id,...}): prefilled from the existing panel.
 *
 * Props:
 *   isOpen  - boolean
 *   onClose - () => void
 *   onSave  - (payload) => Promise<void>  — parent owns the API call
 *   panel   - existing Panel row for edit mode, null for create
 *   saving  - boolean
 *   fieldErrors - array of Pydantic 422 error objects ({loc, msg}) to surface
 *                 inline; empty/undefined renders nothing.
 */
const PanelForm = ({ isOpen, onClose, onSave, panel, saving, fieldErrors = [] }) => {
    const [formData, setFormData] = useState(EMPTY_FORM);

    // Reset on every (re)open DURING RENDER, not in a useEffect. The component
    // stays mounted while closed (it returns null), so formData still holds the
    // last submission; an effect-based reset commits those stale values to the
    // DOM first and clears them a tick later, losing any input that lands in
    // between (see CircuitForm). Setting state while rendering makes React
    // re-render before committing, so stale values never reach the DOM.
    const openKey = isOpen ? `${panel?.id ?? 'new'}` : null;
    const [prevOpenKey, setPrevOpenKey] = useState(null);
    if (openKey !== prevOpenKey) {
        setPrevOpenKey(openKey);
        if (openKey !== null) {
            if (panel && panel.id) {
                setFormData({
                    label: panel.label || '',
                    location: panel.location || '',
                    total_slots: panel.total_slots ?? '',
                    main_breaker_amps: panel.main_breaker_amps ?? '',
                    notes: panel.notes || '',
                });
            } else {
                setFormData(EMPTY_FORM);
            }
        }
    }

    const isEdit = !!(panel && panel.id);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.label.trim()) {
            toast.error('Panel name is required.');
            return;
        }
        const payload = {
            label: formData.label.trim(),
            location: formData.location.trim() || null,
            total_slots: formData.total_slots === '' ? null : Number(formData.total_slots),
            main_breaker_amps: formData.main_breaker_amps === '' ? null : Number(formData.main_breaker_amps),
            notes: formData.notes.trim() || null,
        };
        onSave(payload);
    };

    if (!isOpen) return null;

    const title = isEdit ? 'Edit Panel' : 'Add Panel';
    const labelClass = 'block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-1 theme-transition';
    const inputClass = 'w-full p-3 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)] text-[var(--text-main)] placeholder-[var(--text-muted)] focus:ring-2 focus:ring-[var(--primary)] focus:outline-none theme-transition';

    return (
        <Card className="p-5">
            <h4 className="text-sm font-bold text-[var(--text-main)] uppercase tracking-wide mb-3">
                {title}
            </h4>
            <form onSubmit={handleSubmit} aria-label={title} className="space-y-4">
                <div>
                    <label htmlFor="panel-label" className={labelClass}>
                        Name <span className="text-[var(--error)]">*</span>
                    </label>
                    <input
                        id="panel-label"
                        value={formData.label}
                        onChange={(e) => handleChange('label', e.target.value)}
                        placeholder="e.g. Main Panel, Garage Subpanel"
                        className={inputClass}
                        required
                    />
                </div>

                <div>
                    <label htmlFor="panel-location" className={labelClass}>Location</label>
                    <input
                        id="panel-location"
                        value={formData.location}
                        onChange={(e) => handleChange('location', e.target.value)}
                        placeholder="e.g. Garage, Basement"
                        className={inputClass}
                    />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="panel-total-slots" className={labelClass}>Total Slots</label>
                        <input
                            id="panel-total-slots"
                            type="number"
                            min="1"
                            max="999"
                            value={formData.total_slots}
                            onChange={(e) => handleChange('total_slots', e.target.value)}
                            placeholder="40"
                            className={inputClass}
                        />
                    </div>
                    <div>
                        <label htmlFor="panel-main-breaker-amps" className={labelClass}>Main Breaker (A)</label>
                        <input
                            id="panel-main-breaker-amps"
                            type="number"
                            min="1"
                            max="999"
                            value={formData.main_breaker_amps}
                            onChange={(e) => handleChange('main_breaker_amps', e.target.value)}
                            placeholder="200"
                            className={inputClass}
                        />
                    </div>
                </div>

                <div>
                    <label htmlFor="panel-notes" className={labelClass}>Notes</label>
                    <textarea
                        id="panel-notes"
                        value={formData.notes}
                        onChange={(e) => handleChange('notes', e.target.value)}
                        placeholder="Manufacturer, install date..."
                        rows={2}
                        className={`${inputClass} resize-none`}
                    />
                </div>

                <FieldErrorList errors={fieldErrors} />

                <div className="flex justify-end gap-2 pt-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] rounded-lg transition theme-transition"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        className="px-4 py-2 bg-[var(--primary)] text-white rounded-lg font-medium hover:opacity-90 transition disabled:opacity-50"
                    >
                        {saving ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
                    </button>
                </div>
            </form>
        </Card>
    );
};

export default PanelForm;
